import { BlockEventType, BlockType } from "@sperrdatei/dto";
import { getBlockSize, isValidNumberLength } from "@sperrdatei/dto/utils";
import { useContext, useState } from "react";
import { useNavigate } from "react-router";

import { ApiContext } from "../../api-context";
import { BlockEventList } from "../../components/block-event-list";
import { validateComment } from "../../utils/validate-comment";
import { validateNumberOrBlock } from "../../utils/validate-number-or-block";

import { AddNumberResult } from "./add-number-result";

import type {
  BlockEvent,
  GetPrefixRequestBody,
  GetPrefixSuccessResponse,
  StoreBlockEventRequestBody,
  StoreBlockEventSuccessResponse,
} from "@sperrdatei/dto/types";

export type AddNumbersPageProps = object;

export const AddNumberPage: React.FC<AddNumbersPageProps> = () => {
  const { apiFetch } = useContext(ApiContext);
  const navigate = useNavigate();

  const [multiEdit, setMultiEdit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [number, setNumber] = useState("");
  const [numbersInput, setNumbersInput] = useState("");
  const [numbers, setNumbers] = useState<string[]>([]);
  const [numberError, setNumberError] = useState("");
  const [isPrivate, setIsPrivate] = useState<boolean>(true);
  const [blockType, setBlockType] = useState<BlockType>(BlockType.AllSurveys);
  const [comment, setComment] = useState("");
  const [commentError, setCommentError] = useState("");
  const [showHelp, setShowHelp] = useState(false);
  const [result, setResult] = useState<
    StoreBlockEventSuccessResponse | undefined
  >(undefined);

  const [blockEvents, setBlockEvents] = useState<BlockEvent[]>([]);

  const getValidatedNumber = async (
    value: string,
    line?: number,
  ): Promise<[prefix: string, number: string] | undefined> => {
    const createErrorMesage = (message: string): string => {
      return typeof line === "number" ? `Zeile ${line}: ${message}` : message;
    };

    const numberValidationResult = validateNumberOrBlock(value, isPrivate);
    if (!numberValidationResult.isValid) {
      setNumberError(createErrorMesage(numberValidationResult.errorMessage));
      return;
    }

    if (value.length > 16) {
      setNumberError(createErrorMesage("Die Nummer ist ungültig."));
      return;
    }

    const body: GetPrefixRequestBody = { number: value };
    const response = await apiFetch("/api/number/prefix", {
      method: "POST",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      setNumberError(createErrorMesage("Die Vorwahl ist ungültig."));
      return;
    }

    const { prefix } = (await response.json()) as GetPrefixSuccessResponse;
    const numberWithoutPrefix = value.replace(prefix, "");

    const result = isValidNumberLength(prefix, numberWithoutPrefix);

    if (result !== true) {
      setNumberError(result);
      return;
    }

    return [prefix, numberWithoutPrefix];
  };

  const addBlockEvents = async (): Promise<void> => {
    const events: BlockEvent[] = [];

    if (numbers.length > 50) {
      setNumberError(
        `Pro Sammel-Eingabe dürfen maximal 50 Einträge erfasst werden`,
      );
      return;
    }

    for (let i = 0; i < numbers.length; i++) {
      const validatedNumber = await getValidatedNumber(numbers[i], i + 1);

      if (!validatedNumber) {
        return;
      }

      if (getBlockSize(validatedNumber[1]) > 1) {
        setNumberError(
          `Zeile ${i + 1}: In Sammel-Eingaben sind nur einfache Blöcke mit einem Platzhalter zulässig`,
        );
        return;
      }

      const isDuplicate = events.some(
        (e) =>
          e.prefix === validatedNumber[0] && e.number === validatedNumber[1],
      );

      if (!isDuplicate) {
        events.push({
          eventType: BlockEventType.Block,
          prefix: validatedNumber[0],
          number: validatedNumber[1],
          isPrivate: isPrivate,
          blockSize: getBlockSize(validatedNumber[1]),
          blockType: blockType,
          comment: comment,
        });
      }
    }

    clearForm();
    setBlockEvents([...blockEvents, ...events]);
  };

  const addBlockEvent = async (): Promise<void> => {
    setNumberError("");

    const validatedNumber = await getValidatedNumber(number, undefined);

    if (!validatedNumber) {
      return;
    }

    const commentValidationResult = validateComment(comment, number);
    if (!commentValidationResult.isValid) {
      setCommentError(commentValidationResult.errorMessage);
      return;
    }

    clearForm();

    const event: BlockEvent = {
      eventType: BlockEventType.Block,
      prefix: validatedNumber[0],
      number: validatedNumber[1],
      isPrivate: isPrivate,
      blockSize: getBlockSize(validatedNumber[1]),
      blockType: blockType,
      comment: comment,
    };

    setBlockEvents([
      ...blockEvents.filter(
        (e) => e.prefix !== event.prefix || e.number !== event.number,
      ),
      event,
    ]);
  };

  const deleteBlockEvent = (event: BlockEvent): void => {
    setBlockEvents(
      blockEvents.filter(
        (e) => e.prefix !== event.prefix || e.number !== event.number,
      ),
    );
  };

  const storeBlockEvents = async (): Promise<void> => {
    setIsLoading(true);

    const body: StoreBlockEventRequestBody = {
      events: blockEvents,
    };

    const response = await apiFetch("/api/block-event", {
      method: "POST",
      body: JSON.stringify(body),
    });

    setIsLoading(false);

    if (!response.ok) {
      clearForm();
      navigate("/error");
      return;
    }

    setMultiEdit(false);
    setResult(await response.json());
  };

  const clearForm = (keepBlocks = false): void => {
    if (!keepBlocks) {
      setBlockEvents([]);
    }

    setNumber("");
    setNumbers([]);
    setNumbersInput("");
    setBlockType(BlockType.AllSurveys);
    setIsPrivate(true);
    setComment("");
    setCommentError("");
    setNumberError("");
    setResult(undefined);
  };

  return (
    <div className="container ms-0">
      <h3 className="page-caption mb-3">Neuerfassung</h3>
      {result ? (
        <>
          <AddNumberResult result={result} />
          <button
            type="button"
            className="btn btn-primary mb-3"
            onClick={() => clearForm()}
          >
            OK
          </button>
        </>
      ) : (
        <>
          <p>
            Hier können einzelne Rufnummern oder ganze Rufnummernblöcke (RNB)
            gesperrt werden.
          </p>

          <div className="col-xxl-7 col-xl-7 col-lg-10 col-xs-12 mt-0 p-0">
            <div className="card card-body border-0 px-0 py-0">
              <p>
                <i className="bi bi-question-square color-primary"></i>{" "}
                <a
                  href="#"
                  className="link-dark"
                  onClick={() => setShowHelp(!showHelp)}
                >
                  Hilfe
                </a>
              </p>
              <div className={showHelp ? "collapse show" : "collapse"}>
                <b>Eingabeformat:</b>
                <ul>
                  <li>Vorwahl mit führender Null (ohne Länderkennung 0049)</li>
                  <li>Nationale Teilnehmernummer/RNB ohne Trennzeichen</li>
                </ul>
                <b>Beispiele:</b>
                <ul>
                  <li>Einzelnummer: 04012345678</li>
                  <li>
                    {" "}
                    Rufnummerblock 03012345***
                    <br />
                    <span className="fs-6 fw-light">
                      oder alternativ 017812345xxx
                    </span>
                  </li>
                </ul>
                <div className="bd-callout bd-callout-info">
                  <p>
                    Hinweis: Bei Blocksperrungen werden automatisch auch kürzere
                    Nummern (z.B. Zentralnummern) mitgesperrt. Dadurch entstehen
                    etwas mehr Nummern als im Block angegeben (z.B. 10er Block →
                    11 Nummern, 100er Block → 111 Nummern, usw.).
                  </p>
                </div>
                <b>Sammel-Eingabe:</b>
                <ul>
                  <li>
                    Aktivieren Sie rechts der Eingabe-Box den Button
                    „Sammel-Eingabe“, um mehrere Nummern auf einmal einzufügen
                    (1 Nummer pro Zeile).
                  </li>
                  <li>
                    Wichtig: Alle Nummern müssen die gleiche Art der Sperrung
                    haben – Mischeingaben sind nicht möglich.
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              addBlockEvent();
            }}
            className="container"
            noValidate
          >
            <div className="row">
              <div className="col-xxl-7 col-xl-7 col-lg-10 col-xs-12 mt-0 p-0">
                <div className="mb-1">
                  <label htmlFor="number" className="form-text">
                    <strong>Nummer</strong>
                  </label>
                  {!multiEdit ? (
                    <>
                      <div className="input-group has-validation mb-3">
                        <input
                          type="text"
                          id="number"
                          value={number}
                          maxLength={15}
                          className={
                            numberError
                              ? "form-control is-invalid"
                              : "form-control"
                          }
                          onChange={(e) => {
                            if (e.target.value !== number) {
                              setNumberError("");
                            }

                            setNumber(e.target.value);
                          }}
                          onInput={(e) => {
                            e.currentTarget.value =
                              e.currentTarget.value.replace(/[^0-9x*]/g, "");
                          }}
                          required
                        />
                        <button
                          className="btn btn-outline-secondary border border-secondary-subtle"
                          type="button"
                          onClick={() => {
                            setMultiEdit(true);
                            clearForm(true);
                          }}
                          title="Sammel-Eingabe aktivieren"
                        >
                          <i className="bi bi-card-text"></i>
                        </button>
                        <div id="numberFeedback" className="invalid-feedback">
                          {numberError}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="input-group has-validation">
                        <textarea
                          id="number"
                          rows={5}
                          cols={20}
                          style={{ resize: "none" }}
                          value={numbersInput}
                          className={
                            numberError
                              ? "form-control is-invalid"
                              : "form-control "
                          }
                          onChange={(e) => {
                            setNumberError("");
                            setNumbersInput(e.target.value);
                            setNumbers(
                              e.target.value
                                .split("\n")
                                .filter((value) => value !== ""),
                            );
                          }}
                          onInput={(e) => {
                            e.currentTarget.value =
                              e.currentTarget.value.replace(/[^0-9\n]/g, "");
                          }}
                        ></textarea>
                        <button
                          className="btn btn-secondary border border-secondary-subtle"
                          type="button"
                          onClick={() => setMultiEdit(false)}
                          title="Sammel-Eingabe deaktivieren"
                        >
                          <i className="bi bi-card-text"></i>
                        </button>
                        <div id="numbersFeedback" className="invalid-feedback ">
                          {numberError}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="row">
              <p className="col form-text mb-1 p-0">
                <strong>Art der Sperrung</strong>
              </p>
            </div>
            <div className="row">
              <button
                type="button"
                className={
                  isPrivate
                    ? "btn mb-1 border-white btn-primary col-xl-2 col-lg-3 col-12"
                    : "btn mb-1 border-white btn-secondary col-xl-2 col-lg-3 col-12"
                }
                onClick={() => {
                  setBlockType(BlockType.AllSurveys);
                  setIsPrivate(true);
                }}
              >
                Privat
                <small style={{ display: "block", whiteSpace: "nowrap" }}>
                  Alle Befragungen
                </small>
              </button>
              <button
                type="button"
                className={
                  !isPrivate && blockType === BlockType.AllSurveys
                    ? "btn mb-1 border-white btn-primary col-xl-2 col-lg-3 col-12"
                    : "btn mb-1 border-white btn-secondary col-xl-2 col-lg-3 col-12"
                }
                onClick={() => {
                  setBlockType(BlockType.AllSurveys);
                  setIsPrivate(false);
                }}
              >
                Gewerblich
                <small style={{ display: "block", whiteSpace: "nowrap" }}>
                  Alle Befragungen
                </small>
              </button>
              <button
                type="button"
                className={
                  !isPrivate && blockType === BlockType.PopulationSurveys
                    ? "btn border-white btn-primary mb-1 col-xl-3 col-lg-4"
                    : "btn border-white btn-secondary mb-1 col-xl-3 col-lg-4"
                }
                onClick={() => {
                  setBlockType(BlockType.PopulationSurveys);
                  setIsPrivate(false);
                }}
              >
                Gewerblich
                <small style={{ display: "block" }}>
                  Bevölkerungsbefragungen
                </small>
              </button>
            </div>
            <div className="row">
              <div className="col-xxl-7 col-xl-7 col-lg-10 col-xs-12 p-0">
                <label htmlFor="comment" className="form-text">
                  <strong>Bemerkung</strong>
                </label>
                <textarea
                  className={
                    commentError ? "form-control is-invalid" : "form-control"
                  }
                  id="comment"
                  rows={1}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  disabled={getBlockSize(number) < 100 || multiEdit}
                ></textarea>
                <div id="commentFeedback" className="invalid-feedback">
                  {commentError}
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-xxl-7 col-xl-7 col-lg-10 col-xs-12 p-0 d-flex justify-content-between mt-3">
                <button
                  type="button"
                  className="btn btn-secondary mb-3"
                  onClick={() => clearForm()}
                >
                  Zurücksetzen
                </button>
                <button
                  type="button"
                  className="btn btn-primary mb-3"
                  onClick={() =>
                    multiEdit ? addBlockEvents() : addBlockEvent()
                  }
                >
                  Erfassen
                </button>
              </div>
            </div>
          </form>
          {!result && blockEvents.length ? (
            <div className="col-xxl-7 col-xl-7 col-lg-10 col-xs-12 p-0">
              <h4 className="sub-headline">Prüfen</h4>
              <BlockEventList
                events={blockEvents}
                selectEvent={(event) => {
                  setNumber(event.prefix + event.number);
                  setBlockType(event.blockType);
                  setIsPrivate(event.isPrivate);
                  setComment(event.comment);
                }}
                deleteEvent={(event) => deleteBlockEvent(event)}
              />
              <div>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => storeBlockEvents()}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span
                      className="spinner-border spinner-border-sm me-1"
                      aria-hidden="true"
                    ></span>
                  ) : null}
                  {isLoading ? "Speichern..." : "Speichern"}
                </button>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};
